import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import * as S from '../CarecoAdminDashboard/CarecoAdminStyle'
import { ChatIcon, SupervisionUserIcon, RightIcon } from 'uiKit/icons/Icons'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import LabelWithTooltip from 'uiKit/LabelWithTooltip/LabelWithTooltip'
import { isProfessionalsNavAllowed } from 'tabs/home/api/usageLimits'

type SupervisionData = {
  compliance?: string | number
  positiveSentiment?: string | number
  negativeSentiment?: string | number
} | null

interface AdoptionSnapshotProps extends RouteComponentProps {
  datas?: {
    supervision?: SupervisionData
    chats?: {
      chatUsage?: string | number
      averageQueries?: string | number
    }
  }
  // QCS hides the Chats card and shows Supervision only.
  hideChats?: boolean
  activeBot?: any
}

/* Below this width the two stats inside a card stack vertically (mobile/tablet) */
const STACK_BREAKPOINT = '1300px'

const SentimentStatItem = styled(S.StatItem)`
  /* Positive / negative sit side by side; add spacing only if they wrap */
  @media (max-width: ${STACK_BREAKPOINT}) {
    & + & {
      margin-top: 12px;
    }
  }
`

const SupervisionStatsContainer = styled(S.FlexBlock)`
  @media (max-width: ${STACK_BREAKPOINT}) {
    flex-direction: column;
  }
`

const SupervisionSentimentsContainer = styled(S.StatItem)`
  border-left: 1px solid var(--color-issabeline-grey);
  padding-left: 24px;

  @media (max-width: ${STACK_BREAKPOINT}) {
    && {
      border-left: none;
      padding-left: 0;
      min-width: 100%;
      margin-top: 16px;
    }
  }
`

const ChatsStatsContainer = styled(S.FlexBlock)`
  @media (max-width: ${STACK_BREAKPOINT}) {
    flex-direction: column;
  }
`

const ChatsStatItem = styled(S.StatItem)`
  @media (max-width: ${STACK_BREAKPOINT}) {
    & + & {
      border-left: none;
      padding-left: 0;
      margin-top: 12px;
    }
  }
`

const roundVal = (val: string | number | undefined): number => Math.round(Number(val) || 0)

const AdoptionSnapshot: React.FC<AdoptionSnapshotProps> = ({
  history,
  match,
  datas,
  hideChats,
  activeBot,
}) => {
  const [supervisionData, setSupervisionData] = useState<SupervisionData>(datas?.supervision ?? null)
  const [chats, setChats] = useState<{ chatUsage?: string | number; averageQueries?: string | number }>(datas?.chats ?? {})
  const showSupervision = isProfessionalsNavAllowed(activeBot)

  useEffect(() => {
    setSupervisionData(datas?.supervision ?? null)
    setChats(datas?.chats ?? {})
  }, [datas])

  const supervision = {
    compliance: roundVal(supervisionData?.compliance),
    positiveSentiment: roundVal(supervisionData?.positiveSentiment),
    negativeSentiment: roundVal(supervisionData?.negativeSentiment),
  }

  const handleNavigateToSupervision = () => {
    const baseUrl = (match as any)?.url || ''
    history.push(`${baseUrl}/supervision`)
  }

  const handleNavigateToChats = () => {
    const baseUrl = (match as any)?.url || ''
    history.push(`${baseUrl}/chats`)
  }

  // QCS already hides Chats; if Supervision is also gated, omit the empty section.
  if (hideChats && !showSupervision) {
    return null
  }

  return (
    <S.Container>
      <S.SectionTitle>Adoption and engagement snapshot</S.SectionTitle>
      <S.FlexBlock style={{ gap: 16, flexWrap: 'wrap', alignItems: 'stretch' }}>
        {/* Chats card */}
        {!hideChats && (
        <S.Card style={{ flex: '1 1 300px', marginRight: 0, minHeight: 184 }}>
          <S.HeaderRow>
            <S.HeaderLeft>
              <S.IconCircle>
                <ChatIcon />
              </S.IconCircle>
              <S.CardSubtitle>
                Chats
                {/* <LabelWithTooltip
                  text="Chats"
                  tooltipId="snapshot-chats-title"
                  tooltipText="Shows overall chat usage and activity across the organisation"
                /> */}
              </S.CardSubtitle>
            </S.HeaderLeft>
            <RightIcon onClick={handleNavigateToChats} />
          </S.HeaderRow>

          <ChatsStatsContainer style={{ gap: 24, flexWrap: 'wrap' }}>
            <ChatsStatItem style={{ flex: '1 1 calc(50% - 12px)' }}>
              <S.StatContent>
                <S.StatLabel>
                  <LabelWithTooltip
                    text="Chats usage"
                    tooltipId="snapshot-chats-usage"
                    tooltipText="Percentage of users who have used chat at least once (Calculated as: Active Users ÷ Total Users)"
                    marginLeft={0}
                  />
                </S.StatLabel>
                <S.CardSubtitle>{roundVal(chats.chatUsage)}%</S.CardSubtitle>
              </S.StatContent>
            </ChatsStatItem>

            <ChatsStatItem style={{ flex: '1 1 calc(50% - 12px)' }}>
              <S.StatContent>
                <S.StatLabel>
                  <LabelWithTooltip
                    text="Average queries"
                    tooltipId="snapshot-average-queries"
                    tooltipText="Average number of questions asked by each active user (Calculated as: Total Queries ÷ Active Users)"
                    marginLeft={0}
                  />
                </S.StatLabel>
                <S.CardSubtitle>{roundVal(chats.averageQueries)}</S.CardSubtitle>
              </S.StatContent>
            </ChatsStatItem>
          </ChatsStatsContainer>
        </S.Card>
        )}

        {/* Supervision card */}
        {showSupervision && (
        <S.Card style={{ flex: '1 1 300px', minHeight: 184 }}>
          <S.HeaderRow>
            <S.HeaderLeft>
              <S.IconCircle>
                <SupervisionUserIcon />
              </S.IconCircle>
              <S.CardSubtitle>
                Supervision
                {/* <LabelWithTooltip
                  text="Supervision"
                  tooltipId="snapshot-supervision-title"
                  tooltipText="Shows overall supervision compliance and sentiment trends across carers."
                /> */}
              </S.CardSubtitle>
            </S.HeaderLeft>
            <RightIcon onClick={handleNavigateToSupervision} />
          </S.HeaderRow>

          <SupervisionStatsContainer style={{ alignItems: 'start', flexWrap: 'wrap', gap: 16 }}>
            <S.StatItem style={{ flex: '1 1 calc(40% - 8px)', minWidth: '180px', paddingRight: 0 }}>
              <S.StatContent>
                <S.StatLabel>
                  <LabelWithTooltip
                    text="Supervision compliance"
                    tooltipId="snapshot-supervision-compliance"
                    tooltipText="Percentage of supervision sessions completed on time (Calculated as: (Total Sessions – Overdue Sessions) ÷ Total Sessions)"
                    marginLeft={0}
                  />
                </S.StatLabel>
                <S.CardSubtitle>{supervision.compliance}%</S.CardSubtitle>
              </S.StatContent>
            </S.StatItem>

            <SupervisionSentimentsContainer style={{ flex: '1 1 calc(50% - 8px)', minWidth: '220px', width: '100%' }}>
              <S.StatContent style={{ width: '100%' }}>
                <S.StatLabel>Supervision sentiments</S.StatLabel>
                <S.FlexBlock style={{ flexWrap: 'wrap' }}>
                  <SentimentStatItem style={{ flex: '1 1 45%', paddingRight: 8, borderLeft: 'none', paddingLeft: 0, minWidth: '90px' }}>
                    <S.StatContent>
                      <S.CardSubtitle>{supervision.positiveSentiment}%</S.CardSubtitle>
                      <S.StatLabel style={{ fontSize: '12px', lineHeight: '18px', whiteSpace: 'nowrap' }}>
                        <LabelWithTooltip
                          text="Positive mood"
                          tooltipId="snapshot-positive-mood"
                          tooltipText="Percentage of submitted sessions with positive feedback or tone (Calculated as: Positive Sessions ÷ Submitted Sessions)"
                          marginLeft={0}
                        />
                      </S.StatLabel>
                    </S.StatContent>
                  </SentimentStatItem>
                  <SentimentStatItem style={{ flex: '1 1 45%', minWidth: '90px', marginTop: '0' }}>
                    <S.StatContent>
                      <S.CardSubtitle>{supervision.negativeSentiment}%</S.CardSubtitle>
                      <S.StatLabel style={{ fontSize: '12px', lineHeight: '18px', whiteSpace: 'nowrap' }}>
                        <LabelWithTooltip
                          text="Negative mood"
                          tooltipId="snapshot-negative-mood"
                          tooltipText="Percentage of submitted sessions with negative feedback or tone (Calculated as: Negative Sessions ÷ Submitted Sessions)"
                          marginLeft={0}
                          place="left"
                        />
                      </S.StatLabel>
                    </S.StatContent>
                  </SentimentStatItem>
                </S.FlexBlock>
              </S.StatContent>
            </SupervisionSentimentsContainer>
          </SupervisionStatsContainer>
        </S.Card>
        )}
      </S.FlexBlock>
    </S.Container>
  )
}

const mapStateToProps = (state: { activeBot?: any }) => ({
  activeBot: state.activeBot,
})

export default connect(mapStateToProps)(withRouter(AdoptionSnapshot))
